import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { agents } from '../../../../../../../../api/agents'
import { createAttachmentTracker, getAgentConfig } from '../agentConfigs'

function stampMessages (messages, timestampMap) {
  let changed = false
  for (let i = 0; i < messages.length; i++) {
    if (!timestampMap.has(i)) {
      timestampMap.set(i, new Date().toISOString())
      changed = true
    }
  }
  if (!changed) return null
  return messages.map((m, i) => ({ ...m, createdAt: m.createdAt || timestampMap.get(i) }))
}

function getInFlightToolMessage (messages, toolMessages) {
  for (let i = messages.length - 1; i >= 0; i--) {
    const message = messages[i]
    if (message?.role !== 'assistant' || !Array.isArray(message.toolCalls) || message.toolCalls.length === 0) {
      continue
    }

    for (const toolCall of message.toolCalls.slice().reverse()) {
      const hasToolResult = messages.slice(i + 1).some(nextMessage => (
        nextMessage?.role === 'tool' && (
          (toolCall.id && nextMessage.toolCallId === toolCall.id) ||
          (toolCall.name && nextMessage.name === toolCall.name)
        )
      ))

      if (!hasToolResult) {
        return toolMessages[toolCall.name] || `Running ${toolCall.name}...`
      }
    }
  }

  return null
}

export function useAgentPanel (agentId, options = {}) {
  const { workspaceContext } = options
  const config = getAgentConfig(agentId)
  const attachmentTracker = useMemo(() => createAttachmentTracker(), [])

  const [threadId, setThreadId] = useState(null)
  const [pendingSend, setPendingSend] = useState(false)
  const [pendingCancel, setPendingCancel] = useState(false)
  const [cancelSent, setCancelSent] = useState(false)
  const [optimisticMessage, setOptimisticMessage] = useState(null)
  const messageCountAtSend = useRef(null)
  const serverMessagesRef = useRef([])
  const timestampMap = useRef(new Map())

  const { mutateAsync: invokeAgent, isError: invokeError, isLoading: isInvoking, reset: resetInvoke } = agents.useInvokeAgent(config.agentId)
  const { mutateAsync: cancelAgentMutation, isLoading: isCancellingMutation } = agents.useCancelAgent(config.agentId)
  const { mutateAsync: resumeAgentMutation, isLoading: isResuming } = agents.useResumeAgent(config.agentId)
  const { data: threadDataRaw, isLoading: isThreadLoading, isFetching: isThreadFetching } = agents.useAgentThread(threadId, {
    enabled: !!threadId,
    agentId: config.agentId
  })
  // Disabling the query leaves `data` at its last value, which can keep an errored
  // thread visible after resetThread(). Gate downstream reads on a live threadId.
  const threadData = threadId ? threadDataRaw : undefined

  const status = threadData?.status
  const serverMessages = useMemo(() => threadData?.messages || [], [threadData?.messages])

  useEffect(() => {
    serverMessagesRef.current = serverMessages
  }, [serverMessages])
  const interrupts = threadData?.interrupts || []
  const isPendingAction = status === 'pending_action'

  useEffect(() => {
    if (!pendingSend) return
    const threadIsProcessing = status === 'pending' || status === 'running'
    const messagesIncreased = messageCountAtSend.current !== null && serverMessages.length > messageCountAtSend.current
    if (threadIsProcessing || messagesIncreased) {
      setPendingSend(false)
      messageCountAtSend.current = null
    }
  }, [pendingSend, status, serverMessages.length])

  const serverHasOptimistic = optimisticMessage && serverMessages.some(m => {
    if (m.role !== 'user') return false
    let text = ''
    if (typeof m.content === 'string') {
      text = m.content
    } else if (Array.isArray(m.content)) {
      text = m.content.find(c => c.type === 'text')?.text || ''
    }
    return text === optimisticMessage.content
  })

  useEffect(() => {
    if (serverHasOptimistic) {
      setOptimisticMessage(null)
    }
  }, [serverHasOptimistic])

  const hasCancellationMessage = serverMessages.some(m => m.name === 'system' && m.content === 'Request cancelled.')
  const showSyntheticCancel = status === 'cancelled' && !hasCancellationMessage && !pendingSend && !isInvoking

  const messages = useMemo(() => {
    const base = optimisticMessage && !serverHasOptimistic
      ? [...serverMessages, optimisticMessage]
      : serverMessages
    const withCancel = showSyntheticCancel
      ? [...base, { role: 'assistant', name: 'system', content: 'Request cancelled.' }]
      : base
    return stampMessages(withCancel, timestampMap.current) || withCancel.map((m, i) => ({
      ...m,
      createdAt: m.createdAt || timestampMap.current.get(i)
    }))
  }, [serverMessages, optimisticMessage, serverHasOptimistic, showSyntheticCancel])

  const addMessage = useCallback(async (message) => {
    // If the prior thread is unrecoverable (errored on the server, or the last
    // invoke itself failed), silently start a fresh thread for this send so the
    // user just keeps typing rather than getting stuck.
    const priorThreadIsDead = !!threadId && (status === 'error' || invokeError)
    const sendThreadId = priorThreadIsDead ? null : (threadId || null)
    if (priorThreadIsDead) {
      setThreadId(null)
      serverMessagesRef.current = []
      timestampMap.current = new Map()
      if (resetInvoke) resetInvoke()
    }

    const attachments = attachmentTracker.buildAttachments(workspaceContext)
    messageCountAtSend.current = sendThreadId ? serverMessagesRef.current.length : 0
    setPendingSend(true)
    setOptimisticMessage({ role: 'user', content: message, createdAt: new Date().toISOString() })

    try {
      const result = await invokeAgent({
        message,
        threadId: sendThreadId || undefined,
        ...(attachments.length > 0 ? { attachments } : {})
      })
      if (!sendThreadId && result?.threadId) {
        setThreadId(result.threadId)
      }
      return result
    } catch (error) {
      setPendingSend(false)
      setOptimisticMessage(null)
      throw error
    }
  }, [invokeAgent, threadId, status, invokeError, resetInvoke, workspaceContext, attachmentTracker])

  const resumeThread = useCallback(async (action, feedback) => {
    if (!threadId) return
    return resumeAgentMutation({ threadId, action, feedback: feedback || undefined })
  }, [threadId, resumeAgentMutation])

  const cancelThread = useCallback(async () => {
    if (!threadId || isInvoking) {
      setPendingCancel(true)
      setCancelSent(true)
      return
    }
    setCancelSent(true)
    try {
      await cancelAgentMutation({ threadId })
    } catch (error) {
      // Thread may have already finished
    }
  }, [threadId, isInvoking, cancelAgentMutation])

  useEffect(() => {
    if (pendingCancel && threadId && !isInvoking) {
      setPendingCancel(false)
      cancelAgentMutation({ threadId }).catch(() => {})
    }
  }, [pendingCancel, threadId, isInvoking, cancelAgentMutation])

  useEffect(() => {
    if (cancelSent && status !== 'pending' && status !== 'running') {
      setCancelSent(false)
    }
  }, [cancelSent, status])

  const isProcessing = status === 'pending' || status === 'running' || isInvoking || pendingSend
  const hasMessages = messages.length > 0
  const isLoadingState = isInvoking || pendingSend || (!hasMessages && threadId && (isThreadLoading || isThreadFetching))

  const processingMessage = isPendingAction
    ? null
    : getInFlightToolMessage(messages, config.toolMessages) ||
      (status === 'pending' ? 'Queued request...' : null) ||
      (isProcessing ? 'Thinking...' : null)

  return {
    addMessage,
    cancelThread,
    isCancelling: isCancellingMutation || pendingCancel || cancelSent,
    resumeThread,
    threadId,
    threadData,
    messages,
    agentStatus: isPendingAction ? 'pending_action' : (isProcessing ? 'processing' : (status || 'idle')),
    isPendingAction,
    isResuming,
    interrupts,
    processingMessage,
    isThreadLoading: isLoadingState,
    isErrored: invokeError || status === 'error',
    errorMessage: threadData?.errorMessage || null,
    config,
    workspaceContext
  }
}
